import type { TableSlot } from '@/hooks/useMenuPermission'

/** 필수 판정에 쓰는 컬럼 모양 — 표 컬럼(core.CoreColumnType)·저장 후보 목록이 모두 맞는다 */
export type RequiredCandidate = { dataIndex?: unknown; title?: unknown; required?: boolean }

const isBlank = (value: unknown) => (typeof value === 'string' ? value.trim() === '' : value == null)

/** 화면관리 설정을 반영한 컬럼 위에 keys 칸 별표를 다시 붙인다 — BE 가 비면 거부하는 칸은 설정이 필수를 꺼도 화면이 막는다(LOGISPRD-17254) */
export const keepRequired = <C extends RequiredCandidate>(columns: C[], keys: ReadonlySet<string>): C[] =>
  columns.map((column) => (keys.has(String(column.dataIndex)) ? { ...column, required: true } : column))

/**
 * 권한 슬롯(usePermissionSlot 값)의 forTable(id).columns 에 keepRequired 를 얹는다. keysByTable 에 없는 표는 그대로.
 * 표 별표(생성본 permCols)와 [저장] 필수 검사가 같은 forTable(id).columns 를 지나므로 둘 다 이 칸을 필수로 본다.
 */
export const withRequired = <S extends { forTable: (id: string) => TableSlot }>(slot: S, keysByTable: Record<string, ReadonlySet<string>>): S => ({
  ...slot,
  forTable: (id: string): TableSlot => {
    const table = slot.forTable(id)
    const keys = keysByTable[id]
    return keys ? { ...table, columns: (columns) => keepRequired(table.columns(columns), keys) } : table
  },
})

/** 1.0 `CommonUtil.onSaveData` 문구 — 행은 주문번호로 짚는다(표 행 번호는 정렬·검색으로 바뀐다). GL13·GL15 [저장] 이 쓴다 */
export const requiredMissingByOrder = (orderNo: string, title: string) => `주문번호 ${orderNo}의 필수값 '${title}' 열이 입력되지 않았습니다. 확인 후 다시 시도해 주세요.`

/** 별표가 붙은 칸의 제목 — 표 컬럼 순서 */
export const requiredTitles = (columns: RequiredCandidate[]): string[] => columns.filter((column) => column.required).map((column) => String(column.title))

/** 행 순서·컬럼 순서로 처음 비어 있는 별표 칸 — 1.0 `CommonUtil.onSaveData` 가 선택 행을 훑던 순서다 */
export const findMissingRequired = <T extends object>(rows: T[], columns: RequiredCandidate[]): { row: T; title: string } | undefined => {
  const required = columns.filter((column) => column.required && typeof column.dataIndex === 'string')
  const hit = rows
    .map((row) => ({ row, column: required.find((column) => isBlank((row as Record<string, unknown>)[column.dataIndex as string])) }))
    .find(({ column }) => column)
  return hit?.column ? { row: hit.row, title: String(hit.column.title) } : undefined
}
